// Lucka g6: sammanvägd programrapport när projektet har flera etapper.
import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import DocumentHeader from "./DocumentHeader";
import { LayoutDashboard } from "lucide-react";

interface Props {
  projectId: string;
}

interface StageRow {
  id: string;
  name: string;
  status: string;
  start_date: string | null;
  end_date: string | null;
  bas_u_name: string | null;
  bas_u_email: string | null;
  amp_revision_required: boolean | null;
  amp_reviewed_at: string | null;
}

interface StageStats {
  workPreps: number;
  workPrepsApproved: number;
  rounds: number;
  incidentsOpen: number;
}

const fmt = (d: string | null) => (d ? new Date(d).toLocaleDateString("sv-SE") : "–");
const pct = (done: number, total: number) => (total === 0 ? 0 : Math.round((done / total) * 100));

const ProgramOverview: React.FC<Props> = ({ projectId }) => {
  const [stages, setStages] = useState<StageRow[]>([]);
  const [stats, setStats] = useState<Record<string, StageStats>>({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data: stageData } = await supabase
      .from("project_stages")
      .select(
        "id, name, status, start_date, end_date, bas_u_name, bas_u_email, amp_revision_required, amp_reviewed_at"
      )
      .eq("project_id", projectId)
      .order("sort_order", { ascending: true });
    const rows = (stageData || []) as StageRow[];
    setStages(rows);

    const [preps, rounds, incidents] = await Promise.all([
      supabase
        .from("work_preparations")
        .select("stage_id, review_status")
        .eq("project_id", projectId),
      supabase.from("safety_rounds").select("stage_id").eq("project_id", projectId),
      supabase.from("incident_reports").select("stage_id, status").eq("project_id", projectId),
    ]);

    const map: Record<string, StageStats> = {};
    rows.forEach((s) => {
      map[s.id] = { workPreps: 0, workPrepsApproved: 0, rounds: 0, incidentsOpen: 0 };
    });
    (preps.data || []).forEach((p: { stage_id: string | null; review_status: string | null }) => {
      if (!p.stage_id || !map[p.stage_id]) return;
      map[p.stage_id].workPreps += 1;
      if (p.review_status === "approved") map[p.stage_id].workPrepsApproved += 1;
    });
    (rounds.data || []).forEach((r: { stage_id: string | null }) => {
      if (r.stage_id && map[r.stage_id]) map[r.stage_id].rounds += 1;
    });
    (incidents.data || []).forEach((i: { stage_id: string | null; status: string | null }) => {
      if (i.stage_id && map[i.stage_id] && i.status !== "closed") map[i.stage_id].incidentsOpen += 1;
    });
    setStats(map);
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const closed = stages.filter((s) => s.status === "avslutad").length;
  const programPct = pct(closed, stages.length);
  const missingBasU = stages.filter((s) => !s.bas_u_name && !s.bas_u_email).length;
  const ampPending = stages.filter((s) => s.amp_revision_required && !s.amp_reviewed_at).length;
  const openIncidents = Object.values(stats).reduce((a, s) => a + s.incidentsOpen, 0);

  return (
    <div className="space-y-4">
      <DocumentHeader
        icon={LayoutDashboard}
        title="Programöversikt"
        description="En sammanvägd bild när projektet drivs i flera etapper: hur långt varje etapp kommit, vem som är Bas-U och vad som är öppet just nu."
        legalRef="AFS 2023:3"
        meta={<Badge variant="secondary">{stages.length} etapper</Badge>}
      />

      {loading && <p className="text-sm text-muted-foreground">Laddar…</p>}

      {!loading && stages.length === 0 && (
        <Card>
          <CardContent className="py-6 text-sm text-muted-foreground">
            Projektet har inga etapper. Programöversikten behövs först när arbetet delas upp i
            etapper med olika tider eller olika Bas-U.
          </CardContent>
        </Card>
      )}

      {!loading && stages.length > 0 && (
        <>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Hela programmet</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Progress value={programPct} />
              <p className="text-sm text-muted-foreground">
                {closed} av {stages.length} etapper avslutade ({programPct} %).
              </p>
              <div className="flex flex-wrap gap-2">
                <Badge variant={missingBasU ? "destructive" : "secondary"}>
                  {missingBasU ? `${missingBasU} etapper utan Bas-U` : "Bas-U utsedd i alla etapper"}
                </Badge>
                <Badge variant={ampPending ? "destructive" : "secondary"}>
                  {ampPending ? `${ampPending} väntar AMP-granskning` : "AMP granskad där det krävs"}
                </Badge>
                <Badge variant={openIncidents ? "destructive" : "secondary"}>
                  {openIncidents ? `${openIncidents} öppna tillbud` : "Inga öppna tillbud"}
                </Badge>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Etapp för etapp</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {stages.map((s) => {
                const st = stats[s.id] || {
                  workPreps: 0,
                  workPrepsApproved: 0,
                  rounds: 0,
                  incidentsOpen: 0,
                };
                return (
                  <div key={s.id} className="rounded-md border p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate font-medium">{s.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {fmt(s.start_date)} – {fmt(s.end_date)} ·{" "}
                          {s.bas_u_name || s.bas_u_email || "Bas-U saknas"}
                        </p>
                      </div>
                      <Badge variant={s.status === "avslutad" ? "secondary" : "outline"}>
                        {s.status}
                      </Badge>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2 text-xs">
                      <Badge variant="outline">
                        Arbetsberedningar {st.workPrepsApproved}/{st.workPreps} godkända
                      </Badge>
                      <Badge variant="outline">Skyddsronder {st.rounds}</Badge>
                      <Badge variant={st.incidentsOpen ? "destructive" : "outline"}>
                        Öppna tillbud {st.incidentsOpen}
                      </Badge>
                      {s.amp_revision_required && !s.amp_reviewed_at && (
                        <Badge variant="destructive">AMP-granskning saknas</Badge>
                      )}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default ProgramOverview;
